import axiosInstance, { getNewCancelToken } from '@/services/_AxiosInstance';

export default {
  /**
   * GET `/reports/report/list` — daftar report yang tersedia buat satu asset.
   * SUDAH di-unwrap: yang balik `data.data`, bukan response axios.
   *
   * Daftarnya beda-beda per asset, jangan di-hardcode.
   *
   * Dua kegagalan yang artinya beda:
   * - asset tanpa device dibalas HTTP 404 "data Devices not found!" — dibiarin
   *   nge-throw, pemanggil nangkepnya lalu ngosongin daftar jadi "no reports
   *   available" (src/components/report/ReportDownloadDropdown.vue:73)
   * - ditolak izin dibalas HTTP 200 + `status.errorCode` 412 di body — dibungkus jadi
   *   Error ber-`errorCode` 412 biar pemanggil bisa nampilin pesan yang bener, bukan
   *   diem-diem kosong (ReportDownloadDropdown.vue:76)
   *
   * @param {number|string} massId id asset
   * @returns {Promise<Object[]>} body sudah di-unwrap, `[]` kalau backend gak ngirim
   * @throws {Error} ber-property `errorCode` 412 kalau ditolak izin
   */
  async getReportList(massId) {
    const { data } = await axiosInstance.get('/reports/report/list', {
      params: { devcMassId: massId },
      cancelToken: getNewCancelToken(),
    });

    if (data.status?.errorCode === 412) {
      throw Object.assign(new Error(data.status.message || 'No Access'), { errorCode: 412 });
    }

    return data.data || [];
  },

  /**
   * GET `/reports/report/print/period/{start}/{end}` — unduh berkas report.
   * SUDAH di-unwrap: yang balik `{ blob, filename }`, bukan response axios.
   *
   * `responseType: 'blob'` bikin body ERROR ikut lolos sebagai blob, bukan masuk
   * catch. Content-type gak bisa dipakai buat bedain: API balikin error JSON dengan
   * header `text/html`. Penandanya `content-disposition` — berkas beneran selalu bawa,
   * response error enggak. Kalau headernya gak ada, blobnya dicoba di-parse JSON buat
   * bedain 412 (ditolak izin) dari kegagalan lain.
   *
   * Rentang di bawah atau sama dengan 86400 detik otomatis minta `dataTables: true`.
   *
   * @param {number|string} start unix detik
   * @param {number|string} end unix detik
   * @param {number|string} massId id asset
   * @param {string[]} [reportType=[]] daftar report yang diminta, dari getReportList
   * @returns {Promise<{blob: Blob, filename: string}>} `filename` dicabut dari header
   *   content-disposition
   * @throws {Error} ber-property `errorCode` 412 kalau ditolak izin, atau Error polos
   *   'Report generation failed' kalau blobnya bukan berkas dan bukan JSON 412
   *   (src/components/report/ReportDownloadDropdown.vue:112)
   */
  async getReports(start, end, massId, reportType = []) {
    const params = {
      devcMassId: massId,
      aggregatedUnit: 'HOUR',
      aggregatedLength: 1,
      reports: reportType,
    };
    const interval = parseInt(end) - parseInt(start);
    if (interval <= 86400) params.dataTables = true;

    const response = await axiosInstance.get(`/reports/report/print/period/${start}/${end}`, {
      params,
      responseType: 'blob',
      cancelToken: getNewCancelToken(),
    });

    const disposition = response.headers['content-disposition'] || '';
    const match = disposition.match(/filename="?([^";]+)"?/);
    if (!match) {
      let errorCode = null;
      try {
        errorCode = JSON.parse(await response.data.text())?.status?.errorCode;
      } catch (e) {
        // bukan JSON, biarin errorCode null
      }
      if (errorCode === 412) {
        throw Object.assign(new Error('No Access'), { errorCode: 412 });
      }
      throw new Error('Report generation failed');
    }

    return {
      blob: response.data,
      filename: match[1],
    };
  },
};
